// Iter 127 — Office / timezone helpers (frontend mirror of
// backend/office_time.py). All conversions use the browser's native
// `Intl.DateTimeFormat` engine so we don't need to ship a full tz DB
// or pull moment/date-fns-tz.
//
// The firm currently has two live offices:
//   * London (UK)    — Europe/London   (GMT / BST)
//   * Colombo (SL)   — Asia/Colombo    (SLT, UTC+5:30)
// India / Pakistan values are kept for legacy `User.office` records.
//
// UK statutory deadlines (Companies House / HMRC / VAT) always render
// in Europe/London — that's handled at the call site, not here.

export const OFFICE_TZ = {
  "UK":        "Europe/London",
  "Sri Lanka": "Asia/Colombo",
  "India":     "Asia/Kolkata",
  "Pakistan":  "Asia/Karachi",
};

export const OFFICE_LABEL = {
  "UK":        "London",
  "Sri Lanka": "Colombo",
  "India":     "India",
  "Pakistan":  "Pakistan",
};

// Small abbreviation for zone captions (e.g. "London BST" chip).
// Falls back to whatever `Intl` gives us in-browser at runtime.
export const OFFICE_SHORT = {
  "UK":        "UK",
  "Sri Lanka": "SL",
  "India":     "IN",
  "Pakistan":  "PK",
};

export const DEFAULT_OFFICE = "UK";

// The two "live" offices we surface in the header strip. The others
// remain valid `office` values but aren't shown as fixed columns.
export const LIVE_OFFICES = ["UK", "Sri Lanka"];

export function tzFor(office) {
  return OFFICE_TZ[office] || OFFICE_TZ[DEFAULT_OFFICE];
}

export function officeLabel(office) {
  return OFFICE_LABEL[office] || office || "";
}

/** "HH:MM" wall-clock time in the given office right now. */
export function nowHHMMInOffice(office, at = new Date()) {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: tzFor(office),
  }).format(at);
}

/** "Fri 24 Jan" style date in the given office right now. */
export function nowDateInOffice(office, at = new Date()) {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone: tzFor(office),
  }).format(at);
}

/**
 * `YYYY-MM-DD` for *today* in the given office.
 * Uses `en-CA` because it natively formats as ISO-8601.
 */
export function todayIsoInOffice(office, at = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: tzFor(office),
  }).format(at);
}

/** Convenience: use the given user's office (defaults to UK). */
export function todayIsoForUser(user) {
  return todayIsoInOffice(user?.office || DEFAULT_OFFICE);
}

/** Wall-clock hour (0–23) in the given office. */
export function nowHourInOffice(office, at = new Date()) {
  return parseInt(
    new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      hour12: false,
      timeZone: tzFor(office),
    }).format(at),
    10,
  );
}

/**
 * Zone abbreviation for the given office at `at` (e.g. "GMT", "BST",
 * "IST", "PKT"). Falls back to a short constant if the browser omits
 * the abbreviation.
 */
export function zoneAbbr(office, at = new Date()) {
  const tz = tzFor(office);
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(at);
    const p = parts.find((x) => x.type === "timeZoneName");
    if (p?.value) return p.value;
  } catch (_) {}
  return OFFICE_SHORT[office] || "";
}
